import React from "react";
import { View, StyleSheet, TouchableOpacity, useWindowDimensions } from "react-native";
import { ChatCircleTextIcon } from "@phosphor-icons/react";
import CustomText from "../../../common/general/CustomText/CustomText";
import { colors, radii, shadowStyles, spacing } from "../../../../styles/design-system";
import { formatPercentage } from "../../../../utils/analyticsUtils";

interface StudentInsightsProfileHeaderProps {
  studentName: string;
  email: string;
  lastActive: string;
  enrolledDate: string;

  major?: string;
  year?: string;
  advisor?: string;

  riskStatus?: "At Risk" | "Watch" | "On Track";
  sentiment?: "Negative" | "Neutral" | "Positive";
  probation?: boolean;
  overallProgress: number;
  onSendMessage?: () => void;
}

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const StudentInsightsProfileHeader: React.FC<StudentInsightsProfileHeaderProps> = ({
  studentName,
  email,
  lastActive,
  enrolledDate,
  major,
  year,
  advisor,
  riskStatus,
  sentiment,
  probation,
  overallProgress,
  onSendMessage,
}) => {
  const { width } = useWindowDimensions();

  const isTablet = width < 1024;
  const isMobile = width < 768;

  return (
    <View style={[styles.container, shadowStyles.sm]}>
      <View style={[styles.topRow, isTablet && { flexDirection: "column", gap: 16 }]}>
        <View style={styles.userInfoContainer}>
          <View
            style={[
              styles.avatar,
              isMobile && {
                width: 48,
                height: 48,
                borderRadius: 24,
              },
            ]}>
            <CustomText
              text={getInitials(studentName)}
              size="l"
              weight="bold"
              style={styles.avatarText}
              useTranslationText={false}
            />
          </View>

          <View style={styles.userDetails}>
            <CustomText text={studentName} size="xl" weight="bold" useTranslationText={false} style={styles.name} />

            {(major || year || advisor) && (
              <CustomText
                text={`${major ?? ""}${major && year ? " • " : ""}${year ?? ""}${
                  (major || year) && advisor ? " • " : ""
                }${advisor ? `Advisor: ${advisor}` : ""}`}
                size="s"
                useTranslationText={false}
                style={styles.subtitle}
              />
            )}

            <View style={styles.tagsContainer}>
              {riskStatus && (
                <View style={[styles.tag, styles.errorTag]}>
                  <CustomText text={riskStatus} size="xs" useTranslationText={false} style={styles.errorTagText} />
                </View>
              )}

              {sentiment && (
                <View style={[styles.tag, styles.errorTag]}>
                  <CustomText text={sentiment} size="xs" useTranslationText={false} style={styles.errorTagText} />
                </View>
              )}

              {probation && (
                <View style={[styles.tag, styles.errorTag]}>
                  <CustomText text="Probation" size="xs" useTranslationText={false} style={styles.errorTagText} />
                </View>
              )}

              <CustomText
                text={`Overall Progress: ${formatPercentage(overallProgress)}`}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.progressText}
              />
            </View>

            <CustomText
              text={`${email}   •   Last active: ${lastActive}   •   Enrolled: ${enrolledDate}`}
              size="s"
              useTranslationText={false}
              style={styles.metadata}
            />
          </View>
        </View>

        <TouchableOpacity
          style={[styles.messageButton, isTablet && { alignSelf: "flex-end", marginTop: 12 }]}
          onPress={onSendMessage}>
          <ChatCircleTextIcon size={18} color={colors.white} weight="fill" />
          <CustomText
            text="Send Message"
            size="s"
            weight="bold"
            useTranslationText={false}
            style={styles.messageButtonText}
          />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    padding: spacing.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    marginBottom: spacing.lg,
  },

  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },

  userInfoContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.lg,
    flex: 1,
  },

  avatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: "#C7D2FE",
    justifyContent: "center",
    alignItems: "center",
    marginRight: spacing.lg,
  },

  avatarText: {
    color: "#3730A3",
  },

  userDetails: {
    flex: 1,
    gap: 6,
  },

  name: {
    color: colors.grey[900],
    fontSize: 24,
  },

  subtitle: {
    color: colors.grey[600],
  },

  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    marginVertical: 4,
  },

  tag: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: radii.full,
    borderWidth: 1,
  },

  errorTag: {
    backgroundColor: "#FEF2F2",
    borderColor: "#FECACA",
  },

  errorTagText: {
    color: "#EF4444",
  },

  progressText: {
    color: colors.grey[700],
    marginLeft: 4,
  },

  metadata: {
    color: colors.grey[500],
    marginTop: 4,
    flexWrap: "wrap",
  },

  messageButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#3B82F6",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 8,
  },

  messageButtonText: {
    color: colors.white,
  },
});

export default StudentInsightsProfileHeader;
